import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Gift, Copy, MessageCircle, Share2, PartyPopper, Info, CheckCircle2, Clock, XCircle, ShieldCheck, Smartphone, Send, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Analytics } from "../lib/analytics";
import { referralWhatsappMessage } from "../lib/referralMessage";

const OPERATORS = ["Jio", "Airtel", "Vi (Vodafone Idea)", "BSNL", "MTNL", "Other"];
const CIRCLES = ["Andhra Pradesh", "Assam", "Bihar & Jharkhand", "Chennai", "Delhi", "Gujarat", "Haryana", "Himachal Pradesh", "Jammu & Kashmir", "Karnataka", "Kerala", "Kolkata", "Madhya Pradesh & Chhattisgarh", "Maharashtra & Goa", "Mumbai", "North East", "Odisha", "Punjab", "Rajasthan", "Tamil Nadu", "UP East", "UP West", "West Bengal"];
const WA_MESSAGE = referralWhatsappMessage;

function StatusPill({ status }) {
  const map = {
    SUCCESSFUL:            { label: "✓ Premium Activated", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    REGISTERED:            { label: "⏳ Registered",         cls: "bg-slate-100 text-slate-700 border-slate-200" },
    PAYMENT_PENDING:       { label: "⏳ Payment pending",    cls: "bg-amber-100 text-amber-800 border-amber-200" },
    UNDER_REVIEW:          { label: "🔍 Under review",       cls: "bg-amber-100 text-amber-800 border-amber-200" },
    REFUNDED:              { label: "↩ Refunded",           cls: "bg-orange-100 text-orange-800 border-orange-200" },
    REJECTED_SELF_REFERRAL:{ label: "✗ Self-referral",      cls: "bg-red-100 text-red-800 border-red-200" },
    REJECTED_DUPLICATE:    { label: "✗ Duplicate",          cls: "bg-red-100 text-red-800 border-red-200" },
    REJECTED_ABUSE:        { label: "✗ Rejected",           cls: "bg-red-100 text-red-800 border-red-200" },
  };
  const m = map[status] || { label: status, cls: "bg-slate-100 text-slate-700 border-slate-200" };
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold ${m.cls}`}>{m.label}</span>;
}

export default function Referrals() {
  const nav = useNavigate();
  const { user, loading, isAuthed } = useAuth();
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => { if (!loading && !isAuthed) nav("/login"); }, [loading, isAuthed, nav]);
  useEffect(() => {
    if (!isAuthed) return;
    Analytics.track("REFERRAL_PAGE_VIEWED");
    Promise.all([api.referralsMy(), api.campaignConfig().catch(() => null)])
      .then(([r, c]) => { setData(r); setConfig(c); setBusy(false); })
      .catch(() => setBusy(false));
  }, [isAuthed]);

  const copy = async (text, label, evt) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
      Analytics.track(evt || "REFERRAL_LINK_COPIED");
    }
    catch { toast.error("Copy failed — long-press and copy manually"); }
  };
  const nativeShare = async () => {
    const link = data?.share_link;
    if (!link) return;
    Analytics.track("REFERRAL_CTA_CLICKED", { source: "share_button" });
    if (navigator.share) {
      try {
        await navigator.share({ title: "PM POSHAN Calculator", text: shareMessage, url: link });
        Analytics.track("REFERRAL_WHATSAPP_SHARED", { via: "native_share" });
      } catch {/* user cancelled */}
    } else {
      copy(link, "Referral link", "REFERRAL_LINK_COPIED");
    }
  };
  const trackWhatsApp = () => Analytics.track("REFERRAL_WHATSAPP_SHARED", { via: "wa_me" });

  if (loading || busy || !data) return <div className="p-10 text-center text-slate-500" data-testid="referrals-loading">Loading your referral dashboard…</div>;

  const stats = data.stats || {};
  const successful = stats.successful || 0;
  const milestone = data.milestone || 50;
  const remaining = stats.remaining ?? Math.max(0, milestone - successful);
  const pct = Math.min(100, Math.round((successful / milestone) * 100));
  const rewardGranted = !!stats.reward_granted;
  const rechargeMonths = data.recharge?.recharge_months || 6;
  const rechargeEligible = !!data.recharge?.eligible;
  const rechargeCanSubmit = !!data.recharge?.can_submit;
  const rechargeRequest = data.recharge?.request || null;
  const shareMessage = WA_MESSAGE(data.share_link, config?.share_message);
  const waHref = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;

  const refetch = () => api.referralsMy().then(setData).catch(() => {});

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-5" data-testid="referrals-page">
      {/* Hero */}
      <div>
        <div className="chip chip-green inline-flex items-center gap-1"><Gift size={14}/> Refer &amp; Earn</div>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Refer &amp; Earn — 1 Year Premium + 6 Month Recharge!</h1>
        <p className="mt-1 text-slate-600">Invite fellow teachers to PM POSHAN Calculator. Complete <b>50 successful Premium activations</b> to unlock:</p>
        <div className="mt-3 grid grid-cols-2 gap-2 max-w-md">
          <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-3 text-center">
            <div className="text-2xl">💎</div>
            <div className="font-display font-black text-emerald-900">1 Year Premium</div>
            <div className="text-xs text-emerald-700">FREE (365 days)</div>
          </div>
          <div className="rounded-xl border border-orange-300 bg-orange-50 p-3 text-center">
            <div className="text-2xl">📱</div>
            <div className="font-display font-black text-orange-900">6 Month Recharge</div>
            <div className="text-xs text-orange-700">Any operator</div>
          </div>
        </div>
      </div>

      {/* Reward Card */}
      {rewardGranted ? (
        <div className="rounded-2xl border-2 border-emerald-600 bg-gradient-to-br from-emerald-50 to-amber-50 p-5 sm:p-6" data-testid="referrals-reward-unlocked">
          <div className="flex items-start gap-3">
            <span className="h-12 w-12 rounded-full bg-emerald-600 text-white grid place-items-center shrink-0"><PartyPopper size={24}/></span>
            <div className="flex-1">
              <h2 className="font-display font-extrabold text-xl text-emerald-900">🎉 Reward Unlocked!</h2>
              <p className="text-sm text-emerald-900/90">{milestone} successful referrals completed · 1 Year Premium FREE activated.</p>
              {stats.reward?.new_expiry_date && (
                <p className="mt-1 text-xs text-emerald-800">Premium valid until <b>{new Date(stats.reward.new_expiry_date).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}</b>.</p>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {/* Code / Link card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 space-y-4" data-testid="referrals-code-card">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Your Referral Code</div>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 font-mono text-2xl sm:text-3xl font-extrabold tracking-widest text-emerald-800 bg-emerald-50 rounded-xl border border-emerald-200 px-4 py-3 select-all" data-testid="referrals-code">{data.code}</code>
            <Button onClick={() => copy(data.code, "Code")} className="h-12 bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="referrals-copy-code"><Copy size={16}/> Copy</Button>
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Your Referral Link</div>
          <div className="mt-2 flex flex-col sm:flex-row items-stretch gap-2">
            <input readOnly value={data.share_link} className="flex-1 rounded-xl border border-slate-300 bg-slate-50 px-3 py-2.5 text-sm text-slate-700 select-all" data-testid="referrals-link"/>
            <Button variant="outline" onClick={() => copy(data.share_link, "Link")} className="h-11" data-testid="referrals-copy-link"><Copy size={14}/> Copy Link</Button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <a href={waHref} target="_blank" rel="noopener noreferrer" onClick={trackWhatsApp} data-testid="referrals-whatsapp">
            <Button className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold"><MessageCircle size={16}/> WhatsApp</Button>
          </a>
          <Button variant="outline" onClick={nativeShare} className="h-12" data-testid="referrals-share"><Share2 size={16}/> Share</Button>
          <Button variant="outline" onClick={() => copy(shareMessage, "Message")} className="h-12" data-testid="referrals-copy-message"><Copy size={16}/> Copy Message</Button>
          <Button variant="outline" onClick={() => copy(data.share_link, "Link")} className="h-12" data-testid="referrals-copy2"><Copy size={16}/> Copy Link</Button>
        </div>
      </div>

      {/* Recharge reward */}
      <RechargeCard eligible={rechargeEligible} canSubmit={rechargeCanSubmit} request={rechargeRequest}
                    rechargeMonths={rechargeMonths} successful={successful} milestone={milestone}
                    onSubmitted={refetch}/>

      {/* Progress */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="referrals-progress-card">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Your Progress</div>
          <div className="text-xs text-slate-500">Milestone: <b>{milestone}</b></div>
        </div>
        <div className="mt-2 flex items-baseline gap-2">
          <span className="font-display font-extrabold text-4xl sm:text-5xl text-emerald-800" data-testid="referrals-successful">{successful}</span>
          <span className="text-lg font-semibold text-slate-500">/ {milestone} successful</span>
        </div>
        <div className="mt-3 h-3 w-full rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-emerald-500 to-amber-400 transition-all" style={{ width: `${pct}%` }} data-testid="referrals-progress-bar"/>
        </div>
        <p className="mt-3 text-sm text-slate-700" data-testid="referrals-progress-copy">
          {rewardGranted ? "You've unlocked your 1 YEAR PREMIUM FREE reward — thank you 🙏" :
            remaining === 1 ? <><b>Only 1 more successful referral!</b> Refer one more teacher to unlock your 1-year Premium reward.</> :
            <><b>{remaining} more successful referrals</b> to unlock <b>1 YEAR PREMIUM FREE 🎁</b></>}
        </p>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1"><Clock size={12}/> Pending: <b className="text-slate-700">{stats.pending || 0}</b></span>
          <span className="inline-flex items-center gap-1"><XCircle size={12}/> Refunded: <b className="text-slate-700">{stats.refunded || 0}</b></span>
          <span className="inline-flex items-center gap-1"><Info size={12}/> Total attempted: <b className="text-slate-700">{stats.total || 0}</b></span>
        </div>
      </div>

      {/* Reward tile */}
      <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-5">
        <div className="flex items-start gap-3">
          <span className="h-10 w-10 rounded-lg bg-amber-500 text-white grid place-items-center shrink-0"><Gift size={18}/></span>
          <div>
            <h3 className="font-display font-bold text-amber-900">🎁 1 YEAR PREMIUM FREE</h3>
            <p className="text-sm text-amber-900/80">Awarded automatically once you reach {milestone} <b>successful</b> Premium referrals. Already Premium? We&rsquo;ll add 365 days to your existing expiry — never reset it.</p>
          </div>
        </div>
      </div>

      {/* History */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="referrals-history">
        <div className="px-4 py-3 border-b border-slate-100 text-sm font-bold text-slate-900">Referral history</div>
        {(data.history || []).length === 0 ? (
          <div className="p-6 text-sm text-slate-500 text-center">No referrals yet — share your link to get started.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="p-3 text-xs uppercase font-bold text-slate-500">Referred user</th>
                  <th className="p-3 text-xs uppercase font-bold text-slate-500">Mobile</th>
                  <th className="p-3 text-xs uppercase font-bold text-slate-500">Status</th>
                  <th className="p-3 text-xs uppercase font-bold text-slate-500">Date</th>
                </tr>
              </thead>
              <tbody>
                {data.history.map((h) => (
                  <tr key={h.id} className="border-t border-slate-100" data-testid={`referrals-row-${h.id}`}>
                    <td className="p-3 font-semibold text-slate-900">{h.referred?.name || "New teacher"}</td>
                    <td className="p-3 font-mono text-slate-600">{h.referred?.mobile || "—"}</td>
                    <td className="p-3"><StatusPill status={h.status}/></td>
                    <td className="p-3 text-slate-500 text-xs">{h.successful_at ? new Date(h.successful_at).toLocaleDateString("en-IN") : new Date(h.created_at).toLocaleDateString("en-IN")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Terms */}
      <div id="terms" className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-700 space-y-2 scroll-mt-24" data-testid="referrals-terms">
        <div className="flex items-center gap-2 font-bold text-slate-900"><ShieldCheck size={16}/> Referral Program Terms</div>
        <p><b>Only genuine new users with a verified Premium purchase count toward your reward.</b> Self-referrals, duplicate accounts, refunded payments and abusive activity do not qualify.</p>
        <ul className="list-disc pl-5 space-y-1 text-xs text-slate-600">
          <li>A successful referral requires a new user registration <b>followed by verified Premium activation</b>. Registration alone does not count.</li>
          <li>Each referred user can generate a maximum of one successful referral credit.</li>
          <li>Self-referrals (same account / same mobile / same payment identity) are not eligible.</li>
          <li>Duplicate or fake accounts are not eligible and may be reviewed.</li>
          <li>Refunded, reversed or cancelled payments do not qualify.</li>
          <li>Reaching {milestone} successful referrals unlocks both rewards:
            <b> 1 Year Premium (365 days)</b> — auto-granted; if you already have Premium the 365 days are added to your existing expiry, never resetting it.
            <b> 6-Month Mobile Recharge</b> — requires a manual request from this page and admin approval.
          </li>
          <li>The recharge reward is fulfilled via operator recharge to the mobile number you submit. Delivery timing is subject to admin verification and processing.</li>
          <li>Rewards are granted only after server-side verification. Suspicious or ambiguous cases may be placed <b>Under Review</b> before final decision.</li>
          <li>Referral rewards are non-transferable and non-encashable except as stated.</li>
          <li>PM POSHAN Calculator reserves the right to review, adjust, pause or terminate the referral program at any time. Existing already-granted rewards will be honoured.</li>
        </ul>
        <Link to="/terms" className="text-emerald-700 font-semibold hover:underline">Read the full Terms &amp; Conditions →</Link>
      </div>
    </div>
  );
}

// ── Recharge Reward Card ────────────────────────────────────────────────
function RechargeCard({ eligible, canSubmit, request, rechargeMonths, successful, milestone, onSubmitted }) {
  const [form, setForm] = useState({ mobile_number: "", operator: "", circle: "", plan: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!/^\d{10}$/.test((form.mobile_number || "").replace(/\D/g, "").slice(-10))) {
      toast.error("Enter a valid 10-digit mobile number");
      return;
    }
    if (!form.operator) {
      toast.error("Please select an operator");
      return;
    }
    setSubmitting(true);
    try {
      await api.rechargeRequest({
        mobile_number: form.mobile_number.replace(/\D/g, "").slice(-10),
        operator: form.operator,
        circle: form.circle,
        plan: form.plan,
      });
      toast.success("Recharge request submitted — we'll process it shortly");
      Analytics.track("RECHARGE_REQUEST_SUBMITTED", { operator: form.operator });
      onSubmitted && onSubmitted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit request");
    } finally { setSubmitting(false); }
  };

  // Not eligible yet — locked teaser
  if (!eligible) {
    const remaining = Math.max(0, milestone - successful);
    return (
      <div className="rounded-2xl border-2 border-dashed border-orange-300 bg-orange-50 p-5" data-testid="recharge-locked">
        <div className="flex items-start gap-3">
          <span className="h-10 w-10 rounded-lg bg-orange-500 text-white grid place-items-center shrink-0"><Smartphone size={18}/></span>
          <div>
            <h3 className="font-display font-bold text-orange-900">📱 {rechargeMonths} Month Mobile Recharge — Locked</h3>
            <p className="text-sm text-orange-900/80">
              Complete <b>{remaining}</b> more successful referral{remaining === 1 ? "" : "s"} to unlock this reward.
              Submitting a recharge request will be enabled after your {milestone}th successful Premium activation.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Eligible but has an active request
  if (request && !["COMPLETED", "REJECTED", "CANCELLED"].includes(request.status)) {
    return (
      <div className="rounded-2xl border-2 border-emerald-400 bg-emerald-50 p-5" data-testid="recharge-active">
        <div className="flex items-start gap-3">
          <span className="h-10 w-10 rounded-lg bg-emerald-600 text-white grid place-items-center shrink-0"><Smartphone size={18}/></span>
          <div className="flex-1">
            <h3 className="font-display font-bold text-emerald-900">📱 Recharge Request — {request.status.replaceAll("_", " ")}</h3>
            <div className="text-sm text-emerald-900/90 mt-1 space-y-0.5">
              <div>Mobile: <b>{request.mobile_number}</b> · Operator: <b>{request.operator}</b>{request.circle ? <> · Circle: <b>{request.circle}</b></> : null}</div>
              <div className="text-xs">Submitted on {new Date(request.requested_at).toLocaleString("en-IN")}</div>
              {request.transaction_reference && (
                <div className="text-xs">Reference: <code className="font-mono">{request.transaction_reference}</code></div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Eligible + can submit (either no request or last one terminal)
  const lastCompleted = request && request.status === "COMPLETED";
  return (
    <div className="rounded-2xl border-2 border-orange-400 bg-gradient-to-br from-orange-50 to-amber-50 p-5" data-testid="recharge-form">
      <div className="flex items-start gap-3 mb-3">
        <span className="h-10 w-10 rounded-lg bg-orange-500 text-white grid place-items-center shrink-0"><Smartphone size={18}/></span>
        <div>
          <h3 className="font-display font-black text-orange-900">🎉 {lastCompleted ? "Ready for your next recharge!" : "Recharge Reward Unlocked!"}</h3>
          <p className="text-sm text-orange-900/80">Submit the mobile number you want recharged. Reward: <b>{rechargeMonths} months</b>. Admin will verify and process the recharge.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-widest font-bold text-slate-600">Mobile Number</Label>
          <Input value={form.mobile_number} onChange={(e) => setForm({ ...form, mobile_number: e.target.value })}
                 inputMode="numeric" placeholder="10-digit number" className="h-11 mt-1" data-testid="recharge-mobile"/>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest font-bold text-slate-600">Operator</Label>
          <select value={form.operator} onChange={(e) => setForm({ ...form, operator: e.target.value })}
                  className="mt-1 h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="recharge-operator">
            <option value="">— Select operator —</option>
            {OPERATORS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest font-bold text-slate-600">Circle / State (optional)</Label>
          <select value={form.circle} onChange={(e) => setForm({ ...form, circle: e.target.value })}
                  className="mt-1 h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="recharge-circle">
            <option value="">— Select —</option>
            {CIRCLES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest font-bold text-slate-600">Plan preference (optional)</Label>
          <Input value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}
                 placeholder="e.g. Unlimited / Data plan" className="h-11 mt-1" data-testid="recharge-plan"/>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button onClick={submit} disabled={submitting}
                className="min-h-[48px] bg-orange-600 hover:bg-orange-700 text-white font-bold" data-testid="recharge-submit">
          {submitting ? <Loader2 className="animate-spin" size={16}/> : <Send size={16}/>}
          {submitting ? "Submitting…" : "Submit Recharge Request"}
        </Button>
        <p className="text-xs text-orange-900/70">You'll be able to see the processing status here once submitted.</p>
      </div>
    </div>
  );
}
